import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { AddressTypes } from '@vend/business-components/dist/components/EditCustomerModal/DetailsTab/Address'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { trackCustomHeapEvent } from '@vend/utilities'
import {
  FulfilmentDetail,
  FulfilmentType,
} from '../../../../../models/fulfilment'
import { useRetailerSettingsManager } from '../../../../../ng-compat/hooks'
import {
  HEAP_AUTOCOMPLETE_USED_OBJECT,
  HEAP_DELIVERY_SALE_ACTION,
} from '../../../sale-model.constant'
import { Address } from './Address'
import { EditAddressButtons } from './EditAddressButton/EditAddressButton.type'
import type { AddOrEditAddressButtonType } from './EditAddressButton/EditAddressButton.type'
import {
  AddressFieldValidationResult,
  useAddressValidation,
} from './hooks/useAddressValidation'

interface SaleAddressModalProps {
  formType: AddOrEditAddressButtonType
  fulfillmentDetails?: FulfilmentDetail
  onClose: (fulfillmentDetails?: FulfilmentDetail) => void
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

const EMPTY_ADDRESS: AddressDetails = {
  address_1: '',
  address_2: '',
  suburb: '',
  city: '',
  state: '',
  country_id: '',
  postcode: '',
}

export const areAddressesSame = (
  shippingAddress?: AddressDetails,
  billingAddress?: AddressDetails
) => {
  if (!billingAddress) return true
  if (!shippingAddress) return !billingAddress

  return !(Object.keys(shippingAddress) as Array<keyof AddressDetails>).some(
    key => billingAddress[key] !== shippingAddress[key]
  )
}

export const SaleAddressModal: React.FC<SaleAddressModalProps> = ({
  formType,
  fulfillmentDetails,
  onClose,
  onSave,
}: SaleAddressModalProps) => {
  const [isFormSubmit, setIsFormSubmit] = useState(() => false)
  const [isFormValid, setIsFormValid] = useState(() => true)
  const [hasSameBillingAddress, setHasSameBillingAddress] = useState(
    areAddressesSame(
      fulfillmentDetails?.shippingAddress,
      fulfillmentDetails?.billingAddress
    )
  )

  const retailer = useRetailerSettingsManager().get()
  const {
    streetAddress1Validation,
    streetAddress2Validation,
    cityValidation,
    stateValidation,
    countryValidation,
    zipcodeValidation,
  } = useAddressValidation()

  const [billingAddress, setBillingAddress] = useState(() =>
    formType === EditAddressButtons.add || !fulfillmentDetails?.billingAddress
      ? { ...EMPTY_ADDRESS, country_id: retailer?.country || '' }
      : fulfillmentDetails?.billingAddress
  )
  const [shippingAddress, setShippingAddress] = useState(() =>
    formType === EditAddressButtons.add ||
    formType === EditAddressButtons.addShipping ||
    !fulfillmentDetails?.shippingAddress
      ? { ...EMPTY_ADDRESS, country_id: retailer?.country || '' }
      : fulfillmentDetails?.shippingAddress
  )
  const [billingAddressErrors, setBillingAddressErrors] = useState(
    () => EMPTY_ADDRESS
  )
  const [shippingAddressErrors, setShippingAddressErrors] = useState(
    () => EMPTY_ADDRESS
  )
  const [isAutoCompleteUsed, setIsAutoCompleteUsed] = useState(false)

  // Run validation on initial load when editing existing address
  useEffect(() => {
    if (formType === EditAddressButtons.edit) {
      doFormValidation(shippingAddress, billingAddress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle escape key to prevent propagation to parent modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose(fulfillmentDetails)
      }
    }

    document.addEventListener('keydown', handleEscapeKey, true)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true)
    }
  }, [onClose, fulfillmentDetails])

  const handleClearForm = () => {
    setShippingAddress(EMPTY_ADDRESS)
    setShippingAddressErrors(EMPTY_ADDRESS)
    setBillingAddress(EMPTY_ADDRESS)
    setBillingAddressErrors(EMPTY_ADDRESS)
    setIsFormValid(true)
    setIsFormSubmit(false)
  }

  const isFieldEmpty = (value: string) => !value || value === ''

  const isAddressEmpty =
    (!shippingAddress ||
      Object.values(shippingAddress).every(field => isFieldEmpty(field))) &&
    (!billingAddress ||
      Object.values(billingAddress).every(field => isFieldEmpty(field)))

  const getValidationErrorMessage = (
    validationResult: AddressFieldValidationResult
  ): string => {
    return !validationResult.isValid ? validationResult.errorMessage : ''
  }
  const validateAddress = (address: AddressDetails): AddressDetails => {
    return {
      address_1: getValidationErrorMessage(
        streetAddress1Validation(address.address_1)
      ),
      address_2: getValidationErrorMessage(
        streetAddress2Validation(address.address_2)
      ),
      suburb: '',
      city: getValidationErrorMessage(cityValidation(address.city)),
      state: getValidationErrorMessage(
        stateValidation(address.state, address.country_id)
      ),
      country_id: getValidationErrorMessage(
        countryValidation(address.country_id)
      ),
      postcode: getValidationErrorMessage(
        zipcodeValidation(address.postcode, address.country_id)
      ),
    }
  }

  const doFormValidation = (
    currentShippingAddress = shippingAddress,
    currentBillingAddress = billingAddress,
    currentHasSameBillingAddress = hasSameBillingAddress
  ) => {
    const shippingTempAddressError = validateAddress(currentShippingAddress)
    const hasShippingAddressError = Object.values(
      shippingTempAddressError
    ).some(value => !isFieldEmpty(value))
    setShippingAddressErrors(
      hasShippingAddressError ? shippingTempAddressError : EMPTY_ADDRESS
    )

    let hasBillingAddressError = false
    if (!currentHasSameBillingAddress) {
      const billingTempAddressError = validateAddress(currentBillingAddress)
      hasBillingAddressError = Object.values(billingTempAddressError).some(
        value => !isFieldEmpty(value)
      )
      setBillingAddressErrors(
        hasBillingAddressError ? billingTempAddressError : EMPTY_ADDRESS
      )
    } else {
      setBillingAddressErrors(EMPTY_ADDRESS)
    }

    const isValid = !hasShippingAddressError && !hasBillingAddressError
    setIsFormValid(isValid)
    return isValid
  }

  const saveForm = () => {
    setIsFormSubmit(true)
    if (!doFormValidation()) return

    const newFulfillmentDetail = [
      {
        type: fulfillmentDetails?.type || FulfilmentType.DELIVERY,
        note: fulfillmentDetails?.note || '',
        billingAddress,
        shippingAddress,
        shouldAddNewAddress: false,
      },
    ]

    if (onSave) onSave(newFulfillmentDetail)

    if (isAutoCompleteUsed) {
      trackCustomHeapEvent({
        action: HEAP_DELIVERY_SALE_ACTION,
        object: HEAP_AUTOCOMPLETE_USED_OBJECT,
      })
    }

    onClose(newFulfillmentDetail[0])
  }

  return createPortal(
    <div className="sale-address-modal-wrapper">
      <Modal
        onClose={() => onClose(fulfillmentDetails)}
        size="large"
        className="customer-dialog"
      >
        <ModalHeader>
          {formType === EditAddressButtons.edit ? (
            <T _str="Edit the address for this sale" _context="Modal title" />
          ) : (
            <T _str="Add an address for this sale" _context="Modal title" />
          )}
        </ModalHeader>
        <ModalContent scrollable>
          <p className="vd-mb6">
            {formType === EditAddressButtons.add ? (
              <T
                _str="To use this address for future sales, add it to the customer profile."
                _context="Modal description"
              />
            ) : (
              <T
                _str="The address in the customer profile will remain unchanged."
                _context="Modal description"
              />
            )}
          </p>
          <Address
            key="shipping-address"
            type={AddressTypes.Shipping}
            addressInfo={shippingAddress}
            errors={shippingAddressErrors}
            hasSameBillingAddress={hasSameBillingAddress}
            setHasSameBillingAddress={value => {
              setHasSameBillingAddress(value)
              if (value) {
                setBillingAddress(shippingAddress)
                doFormValidation(shippingAddress, shippingAddress, value)
              } else {
                // Clear billing address when checkbox is unchecked
                const emptyBillingAddress = {
                  ...EMPTY_ADDRESS,
                  country_id: retailer?.country || '',
                }
                setBillingAddress(emptyBillingAddress)
                setBillingAddressErrors(EMPTY_ADDRESS)
                doFormValidation(shippingAddress, emptyBillingAddress, value)
              }
            }}
            onChange={value => {
              setShippingAddress(value)
              if (hasSameBillingAddress) setBillingAddress(value)
              if (isFormSubmit || !isFormValid) {
                const newBillingAddress = hasSameBillingAddress
                  ? value
                  : billingAddress
                doFormValidation(value, newBillingAddress)
              }
            }}
            onAutocompleteSelect={() => {
              setIsAutoCompleteUsed(true)
            }}
          />
          {!hasSameBillingAddress && (
            <Address
              key="billing-address"
              type={AddressTypes.Billing}
              addressInfo={billingAddress}
              errors={billingAddressErrors}
              onChange={value => {
                setBillingAddress(value)
                if (isFormSubmit || !isFormValid) {
                  doFormValidation(shippingAddress, value)
                }
              }}
              onAutocompleteSelect={() => {
                setIsAutoCompleteUsed(true)
              }}
            />
          )}
        </ModalContent>
        <ModalActions>
          <>
            {!isAddressEmpty && (
              <Button
                className="vd-mr2"
                data-track="edit-sale-address-modal-clear"
                modifier="text"
                id="edit-sale-address-modal-clear"
                key="formClear"
                onClick={handleClearForm}
                form="vd-edit-customer-form"
                variant="supplementary"
              >
                <T _str="Clear all fields" _context="Button text" />
              </Button>
            )}
            <Button
              data-track="edit-sale-address-modal-submit"
              id="edit-sale-address-modal-submit"
              key="formSubmit"
              type="submit"
              form="vd-edit-customer-form"
              disabled={!isFormValid}
              onClick={saveForm}
            >
              <T _str="Save Changes" _context="Button text" />
            </Button>
          </>
        </ModalActions>
      </Modal>
    </div>,
    document.body
  )
}
